/**
 * @file EmailConversation.jsx
 * @description EmailConversation component for displaying and managing email conversation.
 * @component EmailConversation
 * @exports EmailConversation - Main component for displaying and managing email conversation.
 */

import { Box, Button, Container, Chip, Alert } from "@mui/material";
import React, { useMemo, useEffect, useState, useCallback } from "react";

import CampaignIcon from "@mui/icons-material/Campaign";

import CardComponent from "../../../common/components/CardComponent";
import ProgressOverlay from "../../../common/components/ProgressOverlay";
import {
  EMAIL_ERRORS,
  EMAIL_STRING,
} from "../../../common/constants/agentModule/emailContants";
import {
  BUTTON_COLORS,
  DISPLAY,
  FONT_WEIGHT,
  SIZES,
  STATUS,
  VARIANTS,
} from "../../../common/constants/commonConstants";
import { getEmailsInfo } from "../../../services/agentModule/email/email-conversation";
import FilterBar from "../common/FilterBar";
import AgentLayout from "../common/AgentLayout";
import {
  TEXT_ERRORS,
  TEXT_SUCCUSS,
} from "../../../common/constants/agentModule/textConstants";
import AssignModal from "../common/AssignModal";
import EndConversationDailog from "../common/EndConversationDailog";
import {
  assignUsers,
  endTextConversation,
} from "../../../services/agentModule/text/text-conversation";
import { useAgentTextActions } from "../agentModel/useAgentTextActions";
import CustomerProfile from "../../callsModule/common/TabProfile";
import CustomerNotes from "../../callsModule/common/TabNotes";
import CustomerHistory from "../../callsModule/common/TabHistory";
import CustomerCallsHistory from "../../callsModule/common/TabCallsHistory";
import CustomerEmails from "../../callsModule/common/TabEmails";
import TabConversation from "../../callsModule/common/TabConversation";
import CustomerTexts from "../../callsModule/common/TabTexts";
import { useNavigate } from "react-router";
import TabEmailConversation from "../../callsModule/common/TabEmailConversation";
import CustomerChats from "../../callsModule/common/TabChats";
import CustomerDataset from "../../callsModule/common/TabDataset.jsx";
import SendText from "../../callsModule/common/SendText.jsx";
import SendEmail from "../../callsModule/common/SendEmail.jsx";
import AddRelatedRecord from "../../callsModule/common/AddRelatedRecord.jsx";

function EmailConversation() {
  const {
    handleAssignClick: agentHandleAssignClick,
    handleEndConversationClick: agentHandleEndConversationClick,
  } = useAgentTextActions();
  const [customers, setCustomers] = useState([]);
  const [users] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [openEndConversationDailog, setOpenEndConversationDailog] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [filterStatus, setFilterStatus] = useState(0); // Default to Active
  const [filterReview, setFilterReview] = useState(0);
  const [filterAssignedTo, setFilterAssignedTo] = useState(-1);
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [openSendText, setOpenSendText] = useState(false);
  const [openSendEmail, setOpenSendEmail] = useState(false);
  const currentUserEmail = JSON.parse(localStorage.getItem("user")); // Assuming you store user info in localStorage
  const agentemail = currentUserEmail?.emailaddress || ""; // Get email if available
  const navigate = useNavigate();
  const tabs = useMemo(
    () => [
      {
        label: "Conversation",
        value: "Conversation",
        component: <TabEmailConversation customerguid={selectedCustomer} />,
      },
      {
        label: "Profile",
        value: "Profile",
        component: (
          <CustomerProfile customerguid={selectedCustomer} agentType="Text" />
        ),
      },
      {
        label: "Dataset",
        value: "Dataset",
        component: <CustomerDataset customerguid={selectedCustomer} />,
      },
      {
        label: "Notes",
        value: "Notes",
        component: <CustomerNotes customerguid={selectedCustomer} />,
      },
      {
        label: "History",
        value: "History",
        component: <CustomerHistory customerguid={selectedCustomer} />,
      },
      { label: "Payments", value: "Payment", component: <div>Payment</div> },
      {
        label: "Calls",
        value: "Calls",
        component: <CustomerCallsHistory customerguid={selectedCustomer} />,
      },
      {
        label: "Texts",
        value: "Text",
        component: (
          <CustomerTexts
            customerguid={selectedCustomer}
            openSendText={openSendText}
            setOpenSendText={setOpenSendText}
          />
        ),
      },
      {
        label: "Emails",
        value: "Emails",
        component: (
          <CustomerEmails
            customerguid={selectedCustomer}
            openSendEmail={openSendEmail}
            setOpenSendEmail={setOpenSendEmail}
          />
        ),
      },
      {
        label: "Chats",
        value: "Chat",
        component: <CustomerChats customerguid={selectedCustomer} />,
      },
    ],
    [selectedCustomer, openSendText, openSendEmail]
  );

  useEffect(() => {
    const fetchEmailRecords = async () => {
      try {
        setIsLoading(true);
        const fetchedEmailRecords = await getEmailsInfo();
        if (fetchedEmailRecords && fetchedEmailRecords.length > 0) {
          setCustomers(fetchedEmailRecords);
        }
      } catch (error) {
        console.error(EMAIL_ERRORS.FETCH_EMAIL_INFO_ERROR, error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchEmailRecords();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleEditClick = (customerguid) => {
    setSelectedCustomer(customerguid);
  };

  const handleAssignClick = (customerdataid) => {
    setOpenAssignModal(true);
    setSelectedCustomer(customerdataid);
  };

  const handleEndConversationClick = (customerdataid) => {
    setOpenEndConversationDailog(true);
    setSelectedCustomer(customerdataid);
  };

  const handleConfirmEndConversation = async () => {
    setOpenEndConversationDailog(false);
    setIsLoading(true);
    const result = await agentHandleEndConversationClick(selectedCustomer);
    if (result.success) {
      await refreshTextInfo();
      setSelectedCustomer(null);
      setSuccessMessage(result.message);
    } else {
      // Optionally handle error
    }
    setIsLoading(false);
  };

  const handleUpdateAssignee = async (id, newAssignee) => {
    setIsLoading(true);
    const result = await agentHandleAssignClick(id, newAssignee);
    setOpenAssignModal(false);
    if (result.success) {
      await refreshTextInfo();
      setSuccessMessage(result.message);
    } else {
      // Optionally handle error
    }
    setIsLoading(false);
  };

  const refreshTextInfo = async () => {
    try {
      setIsLoading(true);
      setSelectedCustomer(null);
      const fetchedTextRecords = await getEmailsInfo();
      if (fetchedTextRecords) setCustomers(fetchedTextRecords);
    } catch (error) {
      console.error(TEXT_ERRORS.FETCH_TEXT_INFO_ERROR, error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCustomers = useMemo(() => {
    return customers.filter((customer) => {
      const matchesSearch = searchTerm
        ? customer.firstname.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.lastname.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.campaignname.toLowerCase().includes(searchTerm.toLowerCase())
        : true;

      const matchesFilter =
        filterStatus === 1
          ? customer.convend === 1
          : filterStatus === 0
          ? customer.convend === 0
          : true;

      const matchesReview =
        filterReview === 1
          ? customer.needsreview === 0
          : filterReview === 0
          ? customer.needsreview === 1
          : true;

      const matchesAssignedTo =
        filterAssignedTo === 0
          ? customer.assignedto === agentemail // Me
          : filterAssignedTo === 1
          ? customer.assignedto && customer.assignedto !== agentemail // Others
          : filterAssignedTo === 2
          ? customer.assignedto === null // Unassigned
          : true;

      return (
        matchesSearch && matchesFilter && matchesReview && matchesAssignedTo
      );
    });
  }, [
    customers,
    searchTerm,
    filterStatus,
    filterReview,
    filterAssignedTo,
    agentemail,
  ]);

  useMemo(() => {
    setSelectedCustomer(null);
  }, [searchTerm, filterStatus, filterReview, filterAssignedTo]);

  const renderEmailConversation = useCallback(() => {
    return filteredCustomers.map((customer) => (
      <CardComponent
        sx={{
          mb: 2,
          cursor: "pointer",
          border:
            selectedCustomer === customer.customerguid
              ? "2px solid #1976d2"
              : "1px solid #ccc",
          backgroundColor:
            selectedCustomer === customer.customerguid ? "#e3f2fd" : "#fff",
        }}
        onClick={() => handleEditClick(customer.customerguid)}
        key={customer.customerdataid}
        title={
          <Box sx={{ display: DISPLAY.FLEX, justifyContent: "space-between" }}>
            <Box sx={{ fontSize: "14px" }}>
              {customer.firstname + " " + customer.lastname}
            </Box>
            <Chip
              icon={<CampaignIcon />}
              label={customer.campaignname}
              size={SIZES.SMALL}
              sx={{ mx: 1, fontWeight: FONT_WEIGHT.NORMAL }}
            />
          </Box>
        }
      />
    ));
  }, [filteredCustomers, selectedCustomer]);


  const menuAction = [
    {
      label: EMAIL_STRING.ASSIGN,
      onClick: () => handleAssignClick(selectedCustomer),
    },
    {
      label: EMAIL_STRING.END_CONVERSATION,
      onClick: () => handleEndConversationClick(selectedCustomer),
    },
    {
      component: <SendText customerguid={selectedCustomer} />,
    },
    {
      component: <SendEmail customerguid={selectedCustomer} />,
    },
    { component: (
        <AddRelatedRecord customerguid={selectedCustomer} />
      ),
    }
  ];

  return (
    <Container maxWidth={false} disableGutters>
      {successMessage && (
        <Alert severity={STATUS.SUCCESS} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {isLoading && <ProgressOverlay />}
      <FilterBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        filterReview={filterReview}
        setFilterReview={setFilterReview}
        filterAssignedTo={filterAssignedTo}
        setFilterAssignedTo={setFilterAssignedTo}
      />

      <AgentLayout
        leftPanel={renderEmailConversation()}
        tabConfig={selectedCustomer ? tabs : []}
        // actions={selectedCustomer ? actions : null}
        menuActions={selectedCustomer ? menuAction : null}
        defaultTab="Conversation"
        setOpenSendText={setOpenSendText}
        setOpenSendEmail={setOpenSendEmail}
        title={selectedCustomer ? "Customer Information" : ""}
        leftPanelTitle="Inbox Panel"
      />

      {openAssignModal && (
        <AssignModal
          open={openAssignModal}
          customerId={selectedCustomer}
          users={users}
          assignedCust={
            customers.find((c) => c.customerdataid === selectedCustomer)
              ?.assignedto
          }
          onAssign={handleUpdateAssignee}
          onClose={() => setOpenAssignModal(false)}
        />
      )}
      {/* Confirmation Dialog */}
      <EndConversationDailog
        open={openEndConversationDailog}
        onClose={() => setOpenEndConversationDailog(false)}
        onConfirm={handleConfirmEndConversation}
      />
    </Container>
  );
}

export default EmailConversation;
